import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Shield, Edit } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RiskProfiling = ({ user, onLogout }) => {
  const [profiles, setProfiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assessmentDialogOpen, setAssessmentDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    attributes: {},
  });
  const [assessmentData, setAssessmentData] = useState({
    client_id: '',
    risk_profile_id: '',
    assessment_data: {},
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profilesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/risk-profiles`),
        axios.get(`${API}/clients`),
      ]);
      setProfiles(profilesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitProfile = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/risk-profiles`, formData);
      toast.success('Risk profile created successfully');
      setDialogOpen(false);
      fetchData();
      setFormData({ name: '', description: '', attributes: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  const handleSubmitAssessment = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/risk-assessments`, assessmentData);
      toast.success('Risk assessment completed successfully');
      setAssessmentDialogOpen(false);
      setAssessmentData({ client_id: '', risk_profile_id: '', assessment_data: {} });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete assessment');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="risk-profiling-container">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              Risk Profiling
            </h1>
            <p className="text-slate-400">Define and manage risk profiles for clients</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="create-profile-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Profile
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Create Risk Profile</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Profile Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Conservative, Moderate, Aggressive"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-slate-800 border-slate-700"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe the risk profile characteristics..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      required
                      className="bg-slate-800 border-slate-700"
                      rows={4}
                      data-testid="profile-description-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="submit-profile-button">
                    Create Profile
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
            
            <Button 
              onClick={() => window.location.href = '/risk-assessment'} 
              className="btn-primary" 
              data-testid="comprehensive-assessment-button"
            >
              <Edit className="w-4 h-4 mr-2" />
              Comprehensive Assessment
            </Button>
            
            <Dialog open={assessmentDialogOpen} onOpenChange={setAssessmentDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-ugmonk-yellow text-ugmonk-grey hover:bg-ugmonk-yellow/10" data-testid="assess-client-button">
                  <Edit className="w-4 h-4 mr-2" />
                  Quick Assess
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Client Risk Assessment</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitAssessment} className="space-y-4">
                  <div>
                    <Label htmlFor="client">Select Client *</Label>
                    <select
                      id="client"
                      value={assessmentData.client_id}
                      onChange={(e) => setAssessmentData({ ...assessmentData, client_id: e.target.value })}
                      required
                      className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700"
                      data-testid="assessment-client-select"
                    >
                      <option value="">Choose a client...</option>
                      {clients.map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.client_name} - {client.type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="risk_profile">Risk Profile *</Label>
                    <select
                      id="risk_profile"
                      value={assessmentData.risk_profile_id}
                      onChange={(e) => setAssessmentData({ ...assessmentData, risk_profile_id: e.target.value })}
                      required
                      className="w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700"
                      data-testid="assessment-profile-select"
                    >
                      <option value="">Choose a risk profile...</option>
                      {profiles.map((profile) => (
                        <option key={profile.id} value={profile.id}>
                          {profile.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Assessment Notes</Label>
                    <Textarea
                      placeholder="Enter assessment notes and observations..."
                      onChange={(e) => setAssessmentData({ ...assessmentData, assessment_data: { notes: e.target.value } })}
                      className="bg-slate-800 border-slate-700"
                      rows={4}
                      data-testid="assessment-notes-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="submit-assessment-button">
                    Complete Assessment
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [...Array(3)].map((_, i) => <div key={i} className="loading-shimmer h-48 rounded-xl" />)
          ) : profiles.length === 0 ? (
            <Card className="col-span-full border-slate-700 bg-slate-900/80">
              <CardContent className="p-12 text-center">
                <Shield className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                <h3 className="text-white text-lg font-semibold mb-2">No Risk Profiles</h3>
                <p className="text-slate-400 mb-4">Create your first risk profile to get started</p>
                <Button onClick={() => setDialogOpen(true)} className="btn-primary">
                  Create Profile
                </Button>
              </CardContent>
            </Card>
          ) : (
            profiles.map((profile) => (
              <Card key={profile.id} className="border-slate-700 bg-slate-800/50 card-hover" data-testid={`profile-card-${profile.id}`}>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-12 h-12 rounded-full bg-ugmonk-yellow/20 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-ugmonk-grey" />
                    </div>
                    <CardTitle className="text-white">{profile.name}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 text-sm">{profile.description}</p>
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <p className="text-xs text-slate-500">Created: {new Date(profile.created_at).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RiskProfiling;